import React, { useState, useEffect, useCallback } from 'react';
import {
  PromptConfigurationContainer,
  PromptConfigurationFormWrapper,
  Title,
  ConfigForm,
} from './PromptConfiguration.styled';
import { usePromptFormContext } from '../../context/PromptFormContext';
import { useStore } from '../../../../../hooks';
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {Alert, Typography, Icon, TextField, Select, CollapsibleList, TextDescription} from "@ann/web-components";
import { PROMPT_CONSTANTS, MOCK_AGENTS, fetchLLMModels, CATEGORIES } from '../../constants/promptConstants';
import { getPromptById } from '../../../../../services';

const PromptConfiguration = ({isVersionPage = false}) => {
  const { authStore } = useStore();
  const tenantId = authStore.selectedAccount.id
  const { promptId } = useParams();           
  const isNew = promptId === 'new' || !promptId;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  
  const [llmModels, setLlmModels] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [descriptionAlert, setDescriptionAlert] = useState(false);  

  const [initialName, setInitialName] = useState('');

  


  const {
    formData,
    errors,
    formState,  
    handleNameChange,
    handleDescriptionChange,
    handleCategoryChange,
    handleLlmModelChange,
    handleInstructionsChange,
    updateFormData,
  } = usePromptFormContext();

  const location = useLocation();
  const isInVersionsPage = /^\/v2\/pms\/config\/[^/]+\/versions$/.test(location.pathname);

   const fetchPrompt = useCallback(async () => {
    if (isNew) return;           
    
    
    await new Promise(resolve => setTimeout(resolve, 2000)); 
    
    try {
      const res = await getPromptById({ id: promptId, tenantId});
      const p   = res.data;      
      updateFormData('name',         p.name);
      updateFormData('description',  p.description);
      updateFormData('instructions', p.promptText);   
      setInitialName(p.name);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Prompt not found.');
    } finally {
      setLoading(false);
    }
  }, [promptId, isNew, updateFormData, tenantId]);
  
  useEffect(() => { fetchPrompt(); }, [fetchPrompt]);
  useEffect(() => {
    const loadLLMModels = async () => {
      setIsLoadingModels(true);
      try {
        const fetchedModels = await fetchLLMModels();
        
        
        if (Array.isArray(fetchedModels) && fetchedModels.length > 0) {
         
          const hasCorrectFormat = fetchedModels.every(model => 
            model && typeof model === 'object' && 'label' in model && 'value' in model
          );
          setLlmModels(fetchedModels);
        }
      } catch (error) {
        setDescriptionAlert('Error fetching LLM models');
        setShowAlert(true);
      } finally {
        setIsLoadingModels(false);
      }
    };

    loadLLMModels();
  }, []);
  
  const handleClose = () => {
    setShowAlert(false);
  };
  
  return (
    <>
      <PromptConfigurationContainer>
        <PromptConfigurationFormWrapper>
          {isInVersionsPage && (
            <Title>
              <Typography category="subtitle" weight="bold" size="medium">
                Prompt Configuration
              </Typography>
            </Title>
          )}
          <ConfigForm>
          <TextField 
            label="Prompt Name" 
            size="medium" 
            placeholder="Give your prompt a clear, descriptive name" 
            value={formData.name} 
            onChange={handleNameChange}  
            supportingText={!isNew && formData.name.trim() === initialName.trim() ? '' : errors.name}  
            state={isVersionPage ? "disabled" : !isNew && formData.name.trim() === initialName.trim() ? "default" : formState.name}
            
          />
          <TextDescription
            maxCharacters={PROMPT_CONSTANTS.MAX_DESCRIPTION_LENGTH}
            label="Description" 
            size="medium" 
            placeholder="Briefly explain what this prompt does and when to use it" 
            value={formData.description}
            onChange={handleDescriptionChange}
            supportingText={errors.description}
            state={isVersionPage ? "disabled" : formState.description}
          />
            <Select
              label="Category"
              placeholder="Select a category"
              width="100%"
              size="medium"
              value={formData.category}
              onChange={handleCategoryChange}
              state={formState.category}
              disabled={isVersionPage}
            >
              <Select.Menu>
                {CATEGORIES.map((category, index) => {
                  const key = category.value || `category-${index}`;
                  const label = String(category.label || "Unknown Category");
                  const value = String(category.value || "");

                  return (
                    <Select.Item key={key} label={label} value={value}>
                      {label}
                    </Select.Item>
                  );
                })}
              </Select.Menu>
            </Select>
            <Select
              label="LLM Model"
              placeholder={
                isLoadingModels ? "Loading models..." : "Select an LLM model"
              }
              width="100%"
              size="medium"
              value={formData.llmModel}
              onChange={handleLlmModelChange}
              state={formState.llmModel}
              disabled={isLoadingModels || isInVersionsPage}
            >
              <Select.Menu>
                {llmModels.map((model, index) => {
                  // Ensure all values are valid strings
                  const key = model.value || `model-${index}`;
                  const label = String(model.label || "Unknown Model");
                  const value = String(model.value || "");

                  return (
                    <Select.Item key={key} label={label} value={value}>
                      {label}
                    </Select.Item>
                  );
                })}
              </Select.Menu>
            </Select>
          </ConfigForm>
        </PromptConfigurationFormWrapper>

        <PromptConfigurationFormWrapper>
          <Title>
            <Typography category="subtitle" weight="bold" size="medium">
              Agents Who Used This Prompt
            </Typography>
          </Title>

          <CollapsibleList minWidth="400px" width="100%">
            {MOCK_AGENTS.map((agent) => (
              <CollapsibleList.Item
                key={agent.id}
                avatar={agent.avatar}
                avatarSize={agent.avatarSize}
              >
                {agent.name}
              </CollapsibleList.Item>
            ))}
          </CollapsibleList>
        </PromptConfigurationFormWrapper>

        <PromptConfigurationFormWrapper>
          <Title>
            <Typography category="subtitle" weight="bold" size="medium">
              Prompts
            </Typography>
          </Title>
          <TextDescription 
            label="Instructions"
            size="medium"
            value={formData.instructions} 
            maxCharacters={PROMPT_CONSTANTS.MAX_INSTRUCTIONS_LENGTH} 
            state={isVersionPage ? "disabled" : formState.instructions} 
            onChange={handleInstructionsChange}
            placeholder="Enter your prompt instructions here..."
            height="172px"
          />
        </PromptConfigurationFormWrapper>
      </PromptConfigurationContainer>

      {showAlert && (
        <Alert
          description={descriptionAlert}
          severity="info"
          maxWidth="500px"
          closable={true}
          autoClose={false}
          autoCloseDelay={5000}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default PromptConfiguration;